import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Container, Form } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  FaBalanceScale,
  FaBuilding,
  FaChartLine,
  FaCheckCircle,
  FaFileInvoiceDollar,
  FaMoneyCheckAlt,
  FaPlus,
  FaReceipt,
  FaSearch,
  FaTrash,
  FaWallet,
} from 'react-icons/fa';
import Navigation from './Navigation';
import AdminPageHeader from './AdminPageHeader';
import AdminDataTable from './AdminDataTable';
import useAuth from '../../hooks/useAuth';
import { hasAccess, isAdminUser, readStoredAdminUser } from './adminAccessControl';
import { buildAccountingReportCenterPath } from './reportCenterLinks';
import './AccountingSetup.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const isSuccessResponse = value => value === 0 || value === '0';

const ASSET_CATEGORY_OPTIONS = [
  { value: 'building', label: 'Bangunan' },
  { value: 'building_improvement', label: 'Renovasi / Improvement' },
  { value: 'gym_equipment', label: 'Alat gym' },
  { value: 'furniture_fixture', label: 'Furniture / Fixture' },
  { value: 'electronic_equipment', label: 'Elektronik / Mesin' },
  { value: 'vehicle', label: 'Kendaraan' },
  { value: 'other_asset', label: 'Aset lain-lain' },
];

const SETTLEMENT_OPTIONS = [
  { value: 'cash', label: 'Bayar tunai' },
  { value: 'bank_transfer', label: 'Transfer / Bank' },
  { value: 'qris', label: 'QRIS' },
  { value: 'other', label: 'Akun asset lain' },
  { value: 'payable', label: 'Catat sebagai hutang' },
];

const STATUS_LABELS = {
  paid: 'Paid',
  unpaid: 'Unpaid',
  partial_paid: 'Partial paid',
  cancelled: 'Cancelled',
};

const WORKSPACE_OPTIONS = [
  {
    key: 'overview',
    title: 'Ringkasan',
    copy: 'Lihat posisi aset, hutang aset, dan kategori pembelian.',
  },
  {
    key: 'asset',
    title: 'Catat Aset',
    copy: 'Input pembelian bangunan, alat gym, renovasi, atau inventaris besar.',
  },
  {
    key: 'payable',
    title: 'Bayar Hutang Aset',
    copy: 'Lunasi pembelian aset yang masih outstanding.',
  },
  {
    key: 'policy',
    title: 'Policy Penyusutan',
    copy: 'Atur umur manfaat per kategori, residu default, dan start rule report.',
  },
  {
    key: 'report',
    title: 'Laporan',
    copy: 'Buka daftar aset, histori pembayaran, dan jurnal terakhir.',
  },
];

const OWNER_REPORT_FLOW = [
  { title: 'Capex intake', copy: 'Cek pembelian aset baru, vendor, dan nilai perolehannya.' },
  { title: 'Open liability', copy: 'Pantau hutang aset yang belum lunas dan jatuh temponya.' },
  { title: 'Category review', copy: 'Lihat aset paling banyak dibeli per kategori pada periode berjalan.' },
  { title: 'Audit trail', copy: 'Pastikan jurnal pembelian dan pembayaran aset tercatat konsisten.' },
];

const getTodayInputValue = () => new Date().toISOString().split('T')[0];

const createEmptyAssetForm = () => ({
  settlement_mode: 'cash',
  expense_date: getTodayInputValue(),
  due_date: getTodayInputValue(),
  vendor_name: '',
  category: 'gym_equipment',
  description: '',
  reference_no: '',
  quantity: '1',
  amount: '',
  asset_account_id: '',
  payment_account_id: '',
  payable_account_id: '',
  payment_method: 'cash',
  notes: '',
});

const createEmptyPayablePaymentForm = () => ({
  document_id: '',
  payment_date: getTodayInputValue(),
  amount: '',
  payment_method: 'bank_transfer',
  payment_account_id: '',
  notes: '',
});

const DEPRECIATION_METHOD_OPTIONS = [
  { value: 'straight_line', label: 'Straight line' },
];

const DEPRECIATION_START_RULE_OPTIONS = [
  { value: 'acquisition_month', label: 'Mulai dari bulan perolehan' },
  { value: 'next_month', label: 'Mulai bulan berikutnya' },
];

const DEFAULT_DEPRECIATION_POLICY_MONTHS = {
  building: 240,
  building_improvement: 120,
  gym_equipment: 60,
  furniture_fixture: 60,
  electronic_equipment: 36,
  vehicle: 60,
  other_asset: 48,
};

const buildDepreciationPolicyUsefulLifeRows = items => {
  const knownCategories = new Set(ASSET_CATEGORY_OPTIONS.map(option => option.value));
  const incomingMap = new Map();

  (Array.isArray(items) ? items : []).forEach(item => {
    const category = String(item?.category || '').trim();
    if (!category) {
      return;
    }

    incomingMap.set(category, {
      category,
      category_label: item?.category_label || humanizeToken(category),
      useful_life_months: String(Math.max(1, Number(item?.useful_life_months || 0) || 0)),
    });
  });

  const rows = ASSET_CATEGORY_OPTIONS.map(option => {
    const existing = incomingMap.get(option.value);
    return {
      category: option.value,
      category_label: option.label,
      useful_life_months: existing?.useful_life_months || String(DEFAULT_DEPRECIATION_POLICY_MONTHS[option.value] || 48),
    };
  });

  Array.from(incomingMap.values())
    .filter(item => !knownCategories.has(item.category))
    .sort((left, right) => left.category.localeCompare(right.category))
    .forEach(item => rows.push(item));

  return rows;
};

const normalizeDepreciationPolicyForm = policy => ({
  method: policy?.method || 'straight_line',
  residual_value_default: String(Number(policy?.residual_value_default || 0)),
  depreciation_start_rule: policy?.depreciation_start_rule || 'acquisition_month',
  useful_life_defaults: buildDepreciationPolicyUsefulLifeRows(policy?.useful_life_defaults),
});

const cloneDepreciationPolicyForm = form => ({
  method: form?.method || 'straight_line',
  residual_value_default: String(form?.residual_value_default ?? '0'),
  depreciation_start_rule: form?.depreciation_start_rule || 'acquisition_month',
  useful_life_defaults: (Array.isArray(form?.useful_life_defaults) ? form.useful_life_defaults : []).map(item => ({
    category: item.category,
    category_label: item.category_label,
    useful_life_months: String(item.useful_life_months ?? ''),
  })),
});

const createEmptyDepreciationPolicyForm = () => normalizeDepreciationPolicyForm(null);

const parseJsonResponse = async response => {
  const raw = await response.text();
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch (error) {
    return {
      error: 1,
      message: raw,
    };
  }
};

const buildAssetApiErrorMessage = (response, data, fallback) => {
  if (response?.status === 404) {
    return 'Endpoint Assets & Investments belum aktif di backend pusat. Restart backend lalu buka ulang halaman ini.';
  }
  return data?.message || fallback;
};

const buildDepreciationPolicyApiErrorMessage = (response, data, fallback) => {
  if (response?.status === 404) {
    return 'Endpoint policy penyusutan belum aktif di backend pusat. Restart backend lalu buka ulang halaman ini.';
  }
  return data?.message || fallback;
};

const formatCurrency = value =>
  `Rp ${Number(value || 0).toLocaleString('id-ID', {
    maximumFractionDigits: 0,
  })}`;

const formatShortDate = value => {
  if (!value) {
    return '-';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  return parsed.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatMonthYear = (year, month) => {
  if (!year || !month) {
    return '-';
  }

  return new Date(year, month - 1, 1).toLocaleDateString('id-ID', {
    month: 'long',
    year: 'numeric',
  });
};

const formatDepreciationUsefulLifeLabel = value => {
  const months = Number(value || 0);
  if (!months) {
    return '-';
  }
  if (months % 12 === 0) {
    const years = months / 12;
    return `${years} tahun`;
  }
  return `${months} bulan`;
};

const getDepreciationMethodLabel = value =>
  DEPRECIATION_METHOD_OPTIONS.find(option => option.value === value)?.label || humanizeToken(value);

const getDepreciationStartRuleLabel = value =>
  DEPRECIATION_START_RULE_OPTIONS.find(option => option.value === value)?.label || humanizeToken(value);

const humanizeToken = value =>
  String(value || '')
    .split('_')
    .filter(Boolean)
    .map(chunk => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join(' ') || '-';

const formatOutletRecord = outlet => ({
  id: String(outlet?.id ?? outlet?.ID ?? ''),
  name: outlet?.outlet_name || outlet?.OutletName || outlet?.name || `Outlet #${outlet?.id || outlet?.ID || '-'}`,
});

const formatCentralAccountRecord = account => ({
  id: String(account?.id ?? account?.ID ?? ''),
  account_id: String(account?.account_id ?? account?.AccountID ?? '').trim(),
  account_name: account?.account_name ?? account?.AccountName ?? '',
  account_type: account?.account_type ?? account?.AccountType ?? '',
});

const getStatusVariant = value => {
  switch (String(value || '').toLowerCase()) {
    case 'paid':
      return '';
    case 'partial_paid':
      return 'is-warning';
    case 'cancelled':
      return 'is-danger';
    default:
      return 'is-danger';
  }
};

function AssetManagementManager() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyAndRefreshToken } = useAuth();
  const currentUser = readStoredAdminUser();
  const canAccess = isAdminUser(currentUser);
  const canOpenAnalyticsCenter = hasAccess('/report-center', currentUser);
  const queryOutletId = useMemo(() => new URLSearchParams(location.search).get('outlet_id') || '', [location.search]);
  const currentPeriod = useMemo(() => {
    const today = new Date();
    return {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      label: formatMonthYear(today.getFullYear(), today.getMonth() + 1),
    };
  }, []);

  const [authToken, setAuthToken] = useState('');
  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState(queryOutletId);
  const [centralAccounts, setCentralAccounts] = useState([]);
  const [assetForm, setAssetForm] = useState(createEmptyAssetForm);
  const [payablePaymentForm, setPayablePaymentForm] = useState(createEmptyPayablePaymentForm);
  const [assetDocuments, setAssetDocuments] = useState([]);
  const [assetPayments, setAssetPayments] = useState([]);
  const [assetSummary, setAssetSummary] = useState(null);
  const [savedDepreciationPolicy, setSavedDepreciationPolicy] = useState(createEmptyDepreciationPolicyForm);
  const [depreciationPolicyForm, setDepreciationPolicyForm] = useState(createEmptyDepreciationPolicyForm);
  const [loading, setLoading] = useState(false);
  const [loadingDepreciationPolicy, setLoadingDepreciationPolicy] = useState(false);
  const [savingAssetDocument, setSavingAssetDocument] = useState(false);
  const [savingAssetPayment, setSavingAssetPayment] = useState(false);
  const [savingDepreciationPolicy, setSavingDepreciationPolicy] = useState(false);
  const [deletingAssetDocumentId, setDeletingAssetDocumentId] = useState(null);
  const [activeWorkspace, setActiveWorkspace] = useState('overview');
  const [assetApiError, setAssetApiError] = useState('');
  const [depreciationPolicyError, setDepreciationPolicyError] = useState('');

  const resolveToken = useCallback(async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) {
      navigate('/loginadmin');
      return '';
    }

    const isValid = await verifyAndRefreshToken(token);
    if (!isValid) {
      navigate('/loginadmin');
      return '';
    }

    const refreshedToken = localStorage.getItem('sessionToken');
    if (!refreshedToken) {
      navigate('/loginadmin');
      return '';
    }

    return refreshedToken;
  }, [navigate, verifyAndRefreshToken]);

  const fetchOutlets = useCallback(async token => {
    const response = await fetch(`${API_BASE_URL}outletlist`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ limit: 200, start: 0 }),
    });
    const data = await response.json();
    if (!response.ok || !isSuccessResponse(data.error)) {
      throw new Error(data.message || 'Failed to fetch outlets');
    }

    const normalizedOutlets = (Array.isArray(data.results) ? data.results : []).map(formatOutletRecord);
    setOutlets(normalizedOutlets);
    setSelectedOutlet(prev => prev || queryOutletId || normalizedOutlets[0]?.id || '');
  }, [queryOutletId]);

  const fetchCentralAccounts = useCallback(async token => {
    const response = await fetch(`${API_BASE_URL}accounting/accounts?limit=500`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = await parseJsonResponse(response);
    if (!response.ok || !isSuccessResponse(data?.error)) {
      throw new Error(data?.message || 'Failed to fetch central accounts');
    }
    setCentralAccounts((Array.isArray(data?.results) ? data.results : []).map(formatCentralAccountRecord));
  }, []);

  const fetchDepreciationPolicy = useCallback(async token => {
    setLoadingDepreciationPolicy(true);
    try {
      const response = await fetch(`${API_BASE_URL}fitness-finance/settings/asset-depreciation-policy`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await parseJsonResponse(response);
      if (!response.ok || !isSuccessResponse(data?.error)) {
        throw new Error(buildDepreciationPolicyApiErrorMessage(response, data, 'Failed to load asset depreciation policy'));
      }

      const normalizedPolicy = normalizeDepreciationPolicyForm(data?.data);
      setSavedDepreciationPolicy(cloneDepreciationPolicyForm(normalizedPolicy));
      setDepreciationPolicyForm(cloneDepreciationPolicyForm(normalizedPolicy));
      setDepreciationPolicyError('');
    } catch (error) {
      console.error('Failed to fetch asset depreciation policy:', error);
      setDepreciationPolicyError(error.message || 'Failed to load asset depreciation policy');
    } finally {
      setLoadingDepreciationPolicy(false);
    }
  }, []);

  const fetchAssetData = useCallback(async (token, outletId) => {
    if (!outletId) {
      return;
    }

    setLoading(true);
    try {
      const [documentsResponse, paymentsResponse, summaryResponse] = await Promise.all([
        fetch(`${API_BASE_URL}fitness-finance/document/list`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            outlet_id: Number(outletId),
            document_scope: 'asset',
            limit: 30,
            offset: 0,
          }),
        }),
        fetch(`${API_BASE_URL}fitness-finance/payment/list`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            outlet_id: Number(outletId),
            document_scope: 'asset',
            limit: 30,
            offset: 0,
          }),
        }),
        fetch(`${API_BASE_URL}fitness-finance/report/asset-summary`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            outlet_id: Number(outletId),
            year: currentPeriod.year,
            month: currentPeriod.month,
            recent_limit: 8,
          }),
        }),
      ]);

      const [documentsData, paymentsData, summaryData] = await Promise.all([
        parseJsonResponse(documentsResponse),
        parseJsonResponse(paymentsResponse),
        parseJsonResponse(summaryResponse),
      ]);

      if (!documentsResponse.ok || !isSuccessResponse(documentsData?.error)) {
        throw new Error(buildAssetApiErrorMessage(documentsResponse, documentsData, 'Failed to load asset documents'));
      }
      if (!paymentsResponse.ok || !isSuccessResponse(paymentsData?.error)) {
        throw new Error(buildAssetApiErrorMessage(paymentsResponse, paymentsData, 'Failed to load asset payments'));
      }
      if (!summaryResponse.ok || !isSuccessResponse(summaryData?.error)) {
        throw new Error(buildAssetApiErrorMessage(summaryResponse, summaryData, 'Failed to load asset summary'));
      }

      setAssetDocuments(Array.isArray(documentsData?.results) ? documentsData.results : []);
      setAssetPayments(Array.isArray(paymentsData?.results) ? paymentsData.results : []);
      setAssetSummary(summaryData?.data?.summary || null);
      setAssetApiError('');
    } catch (error) {
      console.error('Failed to fetch asset data:', error);
      setAssetDocuments([]);
      setAssetPayments([]);
      setAssetSummary(null);
      setAssetApiError(error.message || 'Failed to fetch asset data');
      Swal.fire('Error', error.message || 'Failed to fetch asset data', 'error');
    } finally {
      setLoading(false);
    }
  }, [currentPeriod.month, currentPeriod.year]);

  useEffect(() => {
    if (!canAccess) {
      navigate('/dashboardadmin', { replace: true });
      return;
    }

    let active = true;
    const initialize = async () => {
      const token = await resolveToken();
      if (!token || !active) {
        return;
      }

      setAuthToken(token);
      try {
        await Promise.all([fetchOutlets(token), fetchCentralAccounts(token)]);
        await fetchDepreciationPolicy(token);
      } catch (error) {
        console.error('Failed to initialize asset management page:', error);
        Swal.fire('Error', error.message || 'Failed to initialize asset management page', 'error');
      }
    };

    initialize();

    return () => {
      active = false;
    };
  }, [canAccess, fetchCentralAccounts, fetchDepreciationPolicy, fetchOutlets, navigate, resolveToken]);

  useEffect(() => {
    if (!authToken || !selectedOutlet) {
      return;
    }

    fetchAssetData(authToken, selectedOutlet);
  }, [authToken, fetchAssetData, selectedOutlet]);

  const assetAccounts = useMemo(
    () => centralAccounts.filter(account => String(account.account_type).toLowerCase() === 'asset'),
    [centralAccounts]
  );
  const liabilityAccounts = useMemo(
    () => centralAccounts.filter(account => String(account.account_type).toLowerCase() === 'liability'),
    [centralAccounts]
  );
  const outstandingPayableDocuments = useMemo(
    () => assetDocuments.filter(item => Number(item?.outstanding_amount || 0) > 0),
    [assetDocuments]
  );
  const selectedOutletData = useMemo(
    () => outlets.find(outlet => String(outlet.id) === String(selectedOutlet)) || null,
    [outlets, selectedOutlet]
  );
  const hasLegacyAssetJournalsWithoutDocuments = useMemo(
    () => assetDocuments.length === 0 && Number(assetSummary?.recent_journals?.length || 0) > 0,
    [assetDocuments.length, assetSummary]
  );
  const selectedPayableDocument = useMemo(
    () => outstandingPayableDocuments.find(item => String(item.id) === String(payablePaymentForm.document_id)) || null,
    [outstandingPayableDocuments, payablePaymentForm.document_id]
  );

  useEffect(() => {
    if (assetAccounts.length > 0) {
      setAssetForm(prev => ({
        ...prev,
        asset_account_id: prev.asset_account_id || assetAccounts[0].account_id,
        payment_account_id: prev.payment_account_id || assetAccounts[0].account_id,
      }));
      setPayablePaymentForm(prev => ({
        ...prev,
        payment_account_id: prev.payment_account_id || assetAccounts[0].account_id,
      }));
    }
    if (liabilityAccounts.length > 0) {
      setAssetForm(prev => ({
        ...prev,
        payable_account_id: prev.payable_account_id || liabilityAccounts[0].account_id,
      }));
    }
  }, [assetAccounts, liabilityAccounts]);

  useEffect(() => {
    if (!payablePaymentForm.document_id) {
      return;
    }

    const activeDocument = outstandingPayableDocuments.find(item => String(item.id) === String(payablePaymentForm.document_id));
    if (!activeDocument) {
      return;
    }

    setPayablePaymentForm(prev => (
      prev.amount
        ? prev
        : { ...prev, amount: String(Number(activeDocument.outstanding_amount || 0)) }
    ));
  }, [outstandingPayableDocuments, payablePaymentForm.document_id]);

  const resetAssetForm = useCallback(() => {
    setAssetForm({
      ...createEmptyAssetForm(),
      asset_account_id: assetAccounts[0]?.account_id || '',
      payment_account_id: assetAccounts[0]?.account_id || '',
      payable_account_id: liabilityAccounts[0]?.account_id || '',
    });
  }, [assetAccounts, liabilityAccounts]);

  const resetPayablePaymentForm = useCallback(() => {
    setPayablePaymentForm({
      ...createEmptyPayablePaymentForm(),
      payment_account_id: assetAccounts[0]?.account_id || '',
    });
  }, [assetAccounts]);

  const resetDepreciationPolicyForm = useCallback(() => {
    setDepreciationPolicyForm(cloneDepreciationPolicyForm(savedDepreciationPolicy));
  }, [savedDepreciationPolicy]);

  const handleSaveAssetDocument = useCallback(async event => {
    event.preventDefault();
    if (!selectedOutlet) {
      Swal.fire('Error', 'Pilih outlet lebih dulu.', 'error');
      return;
    }
    if (!assetForm.description.trim()) {
      Swal.fire('Error', 'Nama atau deskripsi aset wajib diisi.', 'error');
      return;
    }
    const assetQuantity = Number(assetForm.quantity || 0);
    if (!Number.isInteger(assetQuantity) || assetQuantity <= 0) {
      Swal.fire('Error', 'Jumlah asset harus berupa bilangan bulat dan lebih dari 0.', 'error');
      return;
    }
    const acquisitionAmount = Number(assetForm.amount || 0);
    if (!Number.isFinite(acquisitionAmount) || acquisitionAmount <= 0) {
      Swal.fire('Error', 'Nilai perolehan total harus lebih dari 0.', 'error');
      return;
    }

    setSavingAssetDocument(true);
    try {
      const response = await fetch(`${API_BASE_URL}fitness-finance/document/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          outlet_id: Number(selectedOutlet),
          document_type: assetForm.settlement_mode === 'payable' ? 'asset_payable_bill' : 'asset_purchase',
          settlement_mode: assetForm.settlement_mode,
          expense_date: assetForm.expense_date,
          due_date: assetForm.settlement_mode === 'payable' ? assetForm.due_date : '',
          vendor_name: assetForm.vendor_name,
          category: assetForm.category,
          description: assetForm.description,
          reference_no: assetForm.reference_no,
          quantity: assetQuantity,
          amount: acquisitionAmount,
          expense_account_id: assetForm.asset_account_id,
          payment_account_id: assetForm.settlement_mode === 'payable' ? '' : assetForm.payment_account_id,
          payable_account_id: assetForm.settlement_mode === 'payable' ? assetForm.payable_account_id : '',
          payment_method: assetForm.settlement_mode === 'payable' ? '' : assetForm.payment_method,
          notes: assetForm.notes,
        }),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok || !isSuccessResponse(data?.error)) {
        throw new Error(buildAssetApiErrorMessage(response, data, 'Failed to save asset document'));
      }

      await fetchAssetData(authToken, selectedOutlet);
      resetAssetForm();
      Swal.fire('Success', data?.message || 'Dokumen aset berhasil disimpan.', 'success');
    } catch (error) {
      console.error('Failed to save asset document:', error);
      Swal.fire('Error', error.message || 'Failed to save asset document', 'error');
    } finally {
      setSavingAssetDocument(false);
    }
  }, [assetForm, authToken, fetchAssetData, resetAssetForm, selectedOutlet]);

  const handleSavePayablePayment = useCallback(async event => {
    event.preventDefault();
    if (!selectedOutlet) {
      Swal.fire('Error', 'Pilih outlet lebih dulu.', 'error');
      return;
    }
    if (!payablePaymentForm.document_id) {
      Swal.fire('Error', 'Pilih dokumen hutang aset dulu.', 'error');
      return;
    }

    setSavingAssetPayment(true);
    try {
      const response = await fetch(`${API_BASE_URL}fitness-finance/payment/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          outlet_id: Number(selectedOutlet),
          document_id: Number(payablePaymentForm.document_id),
          payment_date: payablePaymentForm.payment_date,
          amount: Number(payablePaymentForm.amount || 0),
          payment_method: payablePaymentForm.payment_method,
          payment_account_id: payablePaymentForm.payment_account_id,
          notes: payablePaymentForm.notes,
        }),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok || !isSuccessResponse(data?.error)) {
        throw new Error(buildAssetApiErrorMessage(response, data, 'Failed to save asset payable payment'));
      }

      await fetchAssetData(authToken, selectedOutlet);
      resetPayablePaymentForm();
      Swal.fire('Success', data?.message || 'Pembayaran hutang aset berhasil disimpan.', 'success');
    } catch (error) {
      console.error('Failed to save asset payable payment:', error);
      Swal.fire('Error', error.message || 'Failed to save asset payable payment', 'error');
    } finally {
      setSavingAssetPayment(false);
    }
  }, [authToken, fetchAssetData, payablePaymentForm, resetPayablePaymentForm, selectedOutlet]);

  const handleDeleteAssetDocument = useCallback(async document => {
    if (!selectedOutlet) {
      Swal.fire('Error', 'Pilih outlet lebih dulu.', 'error');
      return;
    }

    const confirmation = await Swal.fire({
      title: 'Hapus dokumen aset?',
      html: `
        <strong>${document?.document_no || 'Dokumen aset'}</strong><br />
        <span>${document?.description || 'Tanpa deskripsi'}</span><br /><br />
        <small>Gunakan hanya untuk salah input. Dokumen hutang aset yang sudah memiliki payment tidak bisa dihapus dari menu ini.</small>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Hapus Dokumen',
      cancelButtonText: 'Batal',
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    setDeletingAssetDocumentId(Number(document?.id || 0));
    try {
      const response = await fetch(`${API_BASE_URL}fitness-finance/document/delete`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          outlet_id: Number(selectedOutlet),
          document_id: Number(document?.id || 0),
        }),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok || !isSuccessResponse(data?.error)) {
        throw new Error(buildAssetApiErrorMessage(response, data, 'Failed to delete asset document'));
      }

      await fetchAssetData(authToken, selectedOutlet);
      Swal.fire('Deleted', data?.message || 'Dokumen aset berhasil dihapus.', 'success');
    } catch (error) {
      console.error('Failed to delete asset document:', error);
      Swal.fire('Error', error.message || 'Failed to delete asset document', 'error');
    } finally {
      setDeletingAssetDocumentId(null);
    }
  }, [authToken, fetchAssetData, selectedOutlet]);

  const handleDepreciationUsefulLifeChange = useCallback((category, nextValue) => {
    setDepreciationPolicyForm(prev => ({
      ...prev,
      useful_life_defaults: prev.useful_life_defaults.map(item => (
        item.category === category
          ? { ...item, useful_life_months: nextValue }
          : item
      )),
    }));
  }, []);

  const handleSaveDepreciationPolicy = useCallback(async event => {
    event.preventDefault();
    if (!authToken) {
      Swal.fire('Error', 'Session login tidak ditemukan.', 'error');
      return;
    }

    const invalidItem = depreciationPolicyForm.useful_life_defaults.find(item => Number(item.useful_life_months || 0) <= 0);
    if (invalidItem) {
      Swal.fire('Error', `Useful life untuk ${invalidItem.category_label || humanizeToken(invalidItem.category)} harus lebih dari 0 bulan.`, 'error');
      return;
    }

    const residualValueDefault = Number(depreciationPolicyForm.residual_value_default || 0);
    if (residualValueDefault < 0) {
      Swal.fire('Error', 'Residu default tidak boleh negatif.', 'error');
      return;
    }

    setSavingDepreciationPolicy(true);
    try {
      const response = await fetch(`${API_BASE_URL}fitness-finance/settings/asset-depreciation-policy`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          method: depreciationPolicyForm.method,
          residual_value_default: residualValueDefault,
          depreciation_start_rule: depreciationPolicyForm.depreciation_start_rule,
          useful_life_defaults: depreciationPolicyForm.useful_life_defaults.map(item => ({
            category: item.category,
            category_label: item.category_label,
            useful_life_months: Number(item.useful_life_months || 0),
          })),
        }),
      });
      const data = await parseJsonResponse(response);
      if (!response.ok || !isSuccessResponse(data?.error)) {
        throw new Error(buildDepreciationPolicyApiErrorMessage(response, data, 'Failed to save asset depreciation policy'));
      }

      const normalizedPolicy = normalizeDepreciationPolicyForm(data?.data);
      setSavedDepreciationPolicy(cloneDepreciationPolicyForm(normalizedPolicy));
      setDepreciationPolicyForm(cloneDepreciationPolicyForm(normalizedPolicy));
      setDepreciationPolicyError('');
      Swal.fire('Success', data?.message || 'Policy penyusutan aset berhasil disimpan.', 'success');
    } catch (error) {
      console.error('Failed to save asset depreciation policy:', error);
      Swal.fire('Error', error.message || 'Failed to save asset depreciation policy', 'error');
    } finally {
      setSavingDepreciationPolicy(false);
    }
  }, [authToken, depreciationPolicyForm]);

  const assetDocumentColumns = [
    {
      name: 'Dokumen',
      sortable: true,
      grow: 1.6,
      cell: row => (
        <span className="table-identity__meta">
          <strong>{row.document_no}</strong>
          <small>{row.description}</small>
        </span>
      ),
    },
    {
      name: 'Kategori',
      width: '210px',
      cell: row => (
        <span className="table-identity__meta">
          <strong>{humanizeToken(row.category)}</strong>
          <small>{row.vendor_name || (row.settlement_mode === 'payable' ? 'Hutang aset' : 'Pembelian langsung')}</small>
        </span>
      ),
    },
    {
      name: 'Tanggal',
      width: '180px',
      cell: row => (
        <span className="table-identity__meta">
          <strong>{formatShortDate(row.expense_date)}</strong>
          <small>{row.due_date ? `Jatuh tempo ${formatShortDate(row.due_date)}` : 'Tidak ada due date'}</small>
        </span>
      ),
    },
    {
      name: 'Posting',
      width: '220px',
      cell: row => (
        <span className="table-identity__meta">
          <strong>{row.expense_account_id || '-'}</strong>
          <small>{row.payment_account_id || row.payable_account_id || '-'}</small>
        </span>
      ),
    },
    {
      name: 'Qty',
      selector: row => Number(row.quantity || 1),
      sortable: true,
      width: '90px',
      cell: row => <strong>{Number(row.quantity || 1).toLocaleString('id-ID')}</strong>,
    },
    {
      name: 'Nilai',
      width: '210px',
      cell: row => (
        <span className="table-identity__meta">
          <strong>{formatCurrency(row.amount)}</strong>
          <small>Terbayar {formatCurrency(row.paid_amount)} | Sisa {formatCurrency(row.outstanding_amount)}</small>
        </span>
      ),
    },
    {
      name: 'Status',
      width: '160px',
      cell: row => (
        <span className={`status-pill ${getStatusVariant(row.status)}`}>
          {STATUS_LABELS[row.status] || humanizeToken(row.status)}
        </span>
      ),
    },
    {
      name: 'Aksi',
      width: '170px',
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      cell: row => {
        const hasAssetPayablePayments =
          row.settlement_mode === 'payable' && Number(row.paid_amount || 0) > 0;
        const isDeleting = Number(deletingAssetDocumentId || 0) === Number(row.id || 0);

        return (
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handleDeleteAssetDocument(row)}
            disabled={isDeleting || hasAssetPayablePayments}
            title={hasAssetPayablePayments ? 'Dokumen sudah memiliki payment hutang aset' : 'Hapus dokumen aset'}
          >
            <FaTrash /> {isDeleting ? 'Deleting...' : 'Hapus'}
          </Button>
        );
      },
    },
  ];

  const assetPaymentColumns = [
    {
      name: 'Payment',
      sortable: true,
      grow: 1.4,
      cell: row => (
        <span className="table-identity__meta">
          <strong>{row.payment_no}</strong>
          <small>{row.document_no || row.document_description || 'Dokumen aset'}</small>
        </span>
      ),
    },
    {
      name: 'Tanggal',
      width: '170px',
      cell: row => (
        <span className="table-identity__meta">
          <strong>{formatShortDate(row.payment_date)}</strong>
          <small>{humanizeToken(row.payment_method)}</small>
        </span>
      ),
    },
    {
      name: 'Nominal',
      width: '180px',
      cell: row => <strong>{formatCurrency(row.amount)}</strong>,
    },
    {
      name: 'Dokumen',
      grow: 1.4,
      cell: row => (
        <span className="table-identity__meta">
          <strong>{row.document_description || '-'}</strong>
          <small>{row.vendor_name || row.document_status || '-'}</small>
        </span>
      ),
    },
  ];

  const recentJournalColumns = [
    {
      name: 'Waktu',
      width: '180px',
      cell: row => (
        <span className="table-identity__meta">
          <strong>{formatShortDate(row.occurred_at)}</strong>
          <small>{humanizeToken(row.source_type)}</small>
        </span>
      ),
    },
    {
      name: 'Deskripsi',
      grow: 1.8,
      cell: row => (
        <span className="table-identity__meta">
          <strong>{row.description}</strong>
          <small>{row.debit_account} -> {row.credit_account}</small>
        </span>
      ),
    },
    {
      name: 'Nilai',
      width: '170px',
      cell: row => <strong>{formatCurrency(row.amount)}</strong>,
    },
  ];

  return (
    <div>
      <Navigation />
      <Container fluid className="admin-page-shell mt-4 px-4">
        <AdminPageHeader
          icon={FaBuilding}
          eyebrow="Assets & Investments"
          title="Assets & Investments"
          description="Workspace khusus untuk pembelian bangunan, alat gym, renovasi, inventaris besar, dan hutang aset supaya tidak tercampur dengan beban usaha."
          breadcrumbs={[
            { label: 'Dashboard', to: '/dashboardadmin' },
            { label: 'Assets & Investments' },
          ]}
          meta={(
            <div className="d-flex flex-wrap gap-2">
              <span className="soft-stat">
                <FaBalanceScale /> {selectedOutletData?.name || 'Select outlet'}
              </span>
              <span className="soft-stat">
                <FaChartLine /> {currentPeriod.label}
              </span>
            </div>
          )}
          actions={(
            <div className="d-flex flex-wrap gap-2">
              <Button
                variant="primary"
                onClick={() => navigate(buildAccountingReportCenterPath('balance-sheet', { outletId: selectedOutlet }))}
                disabled={!canOpenAnalyticsCenter}
              >
                <FaChartLine /> Analytics Center
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => navigate(`/operasional-finance?outlet_id=${selectedOutlet}`)}
                disabled={!selectedOutlet}
              >
                <FaReceipt /> Expenses & Payables
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => navigate(`/accounting-setup?outlet_id=${selectedOutlet}`)}
                disabled={!selectedOutlet}
              >
                <FaWallet /> Accounting Control
              </Button>
            </div>
          )}
        />

        <div className="section-card accounting-filter-card">
          <div className="table-toolbar">
            <div className="control-with-icon search-control accounting-outlet-picker">
              <span className="control-with-icon__icon">
                <FaSearch />
              </span>
              <Form.Select value={selectedOutlet} onChange={event => setSelectedOutlet(event.target.value)}>
                <option value="">Select outlet</option>
                {outlets.map(outlet => (
                  <option key={outlet.id} value={outlet.id}>
                    {outlet.name}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="accounting-filter-card__note">
              Gunakan page ini untuk aset jangka panjang. Bangunan, alat gym, renovasi, dan inventaris besar tidak masuk ke beban usaha rutin.
            </div>
          </div>
        </div>

        {assetApiError ? (
          <div className="section-card accounting-alert-card">
            <div className="accounting-alert-card__copy">
              <span className="page-eyebrow">Connection issue</span>
              <h2>Assets & Investments belum bisa memuat data</h2>
              <p>{assetApiError}</p>
            </div>
            <div className="accounting-alert-card__actions">
              <Button variant="primary" onClick={() => authToken && selectedOutlet && fetchAssetData(authToken, selectedOutlet)}>
                <FaCheckCircle /> Coba lagi
              </Button>
            </div>
          </div>
        ) : null}

        <div className="accounting-kpi-grid">
          <section className="section-card accounting-kpi-card">
            <span className="page-eyebrow">Perolehan bulan ini</span>
            <h3>{formatCurrency(assetSummary?.period_acquisition_total)}</h3>
            <p>Total pembelian aset pada periode berjalan.</p>
          </section>
          <section className="section-card accounting-kpi-card">
            <span className="page-eyebrow">Nilai perolehan tercatat</span>
            <h3>{formatCurrency(assetSummary?.capitalized_total)}</h3>
            <p>Total nilai perolehan aset yang sudah tercatat.</p>
          </section>
          <section className="section-card accounting-kpi-card">
            <span className="page-eyebrow">Hutang aset</span>
            <h3>{formatCurrency(assetSummary?.outstanding_total)}</h3>
            <p>{assetSummary?.open_bills || 0} dokumen pembelian aset masih outstanding.</p>
          </section>
          <section className="section-card accounting-kpi-card">
            <span className="page-eyebrow">Aset sudah dibayar</span>
            <h3>{formatCurrency(assetSummary?.paid_total)}</h3>
            <p>Total pembayaran aset yang sudah terealisasi.</p>
          </section>
        </div>

        <section className="section-card accounting-insight-card">
          <div className="accounting-section-head">
            <div>
              <span className="page-eyebrow">Panduan cepat</span>
              <h2>Pilih proses aset yang ingin dijalankan</h2>
              <p>Untuk user pemula, cukup ikuti satu alur. Sistem akan membedakan aset dan beban secara otomatis.</p>
            </div>
            <span className="status-pill">
              <FaBalanceScale /> Guided mode
            </span>
          </div>

          <div className="accounting-report-flow">
            {WORKSPACE_OPTIONS.map(item => (
              <button
                key={item.key}
                type="button"
                className={`accounting-report-flow__item accounting-report-flow__item--button ${activeWorkspace === item.key ? 'is-active' : ''}`}
                onClick={() => setActiveWorkspace(item.key)}
              >
                <strong>{item.title}</strong>
                <small>{item.copy}</small>
              </button>
            ))}
          </div>
        </section>

        {activeWorkspace === 'asset' ? (
          <div className="accounting-panel-grid">
            <section className="section-card accounting-insight-card">
              <div className="accounting-section-head">
                <div>
                  <span className="page-eyebrow">Input aset</span>
                  <h2>Pembelian bangunan, alat gym, dan aset lain</h2>
                  <p>Pilih beli langsung atau catat sebagai hutang. Sistem akan posting ke akun aset, bukan ke beban usaha.</p>
                </div>
                <span className="status-pill">
                  <FaBuilding /> Asset doc
                </span>
              </div>

              <Form onSubmit={handleSaveAssetDocument}>
                <div className="form-grid">
                  <Form.Group className="field-stack span-4">
                    <Form.Label>Metode pencatatan</Form.Label>
                    <Form.Select
                      value={assetForm.settlement_mode}
                      onChange={event => setAssetForm(prev => ({ ...prev, settlement_mode: event.target.value }))}
                    >
                      {SETTLEMENT_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="field-stack span-4">
                    <Form.Label>Tanggal dokumen</Form.Label>
                    <Form.Control
                      type="date"
                      value={assetForm.expense_date}
                      onChange={event => setAssetForm(prev => ({ ...prev, expense_date: event.target.value }))}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="field-stack span-4">
                    <Form.Label>Kategori aset</Form.Label>
                    <Form.Select
                      value={assetForm.category}
                      onChange={event => setAssetForm(prev => ({ ...prev, category: event.target.value }))}
                    >
                      {ASSET_CATEGORY_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="field-stack span-6">
                    <Form.Label>Vendor / penjual</Form.Label>
                    <Form.Control
                      value={assetForm.vendor_name}
                      onChange={event => setAssetForm(prev => ({ ...prev, vendor_name: event.target.value }))}
                      placeholder="Contoh: Vendor alat gym, developer, kontraktor"
                    />
                  </Form.Group>
                  <Form.Group className="field-stack span-6">
                    <Form.Label>Nomor referensi</Form.Label>
                    <Form.Control
                      value={assetForm.reference_no}
                      onChange={event => setAssetForm(prev => ({ ...prev, reference_no: event.target.value }))}
                      placeholder="No invoice / SPK / kuitansi"
                    />
                  </Form.Group>
                  <Form.Group className="field-stack span-12">
                    <Form.Label>Nama aset / deskripsi</Form.Label>
                    <Form.Control
                      value={assetForm.description}
                      onChange={event => setAssetForm(prev => ({ ...prev, description: event.target.value }))}
                      placeholder="Contoh: 5 treadmill baru untuk outlet A"
                      required
                    />
                  </Form.Group>
                  <Form.Group className="field-stack span-3">
                    <Form.Label>Jumlah asset</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      step="1"
                      value={assetForm.quantity}
                      onChange={event => setAssetForm(prev => ({ ...prev, quantity: event.target.value }))}
                      placeholder="1"
                      required
                    />
                  </Form.Group>
                  <Form.Group className="field-stack span-3">
                    <Form.Label>Nilai perolehan total</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={assetForm.amount}
                      onChange={event => setAssetForm(prev => ({ ...prev, amount: event.target.value }))}
                      placeholder="0"
                      required
                    />
                  </Form.Group>
                  <Form.Group className="field-stack span-6">
                    <Form.Label>Akun aset</Form.Label>
                    <Form.Select
                      value={assetForm.asset_account_id}
                      onChange={event => setAssetForm(prev => ({ ...prev, asset_account_id: event.target.value }))}
                      required
                    >
                      <option value="">Pilih akun aset</option>
                      {assetAccounts.map(account => (
                        <option key={account.account_id} value={account.account_id}>
                          {account.account_id} - {account.account_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  {assetForm.settlement_mode === 'payable' ? (
                    <>
                      <Form.Group className="field-stack span-4">
                        <Form.Label>Jatuh tempo</Form.Label>
                        <Form.Control
                          type="date"
                          value={assetForm.due_date}
                          onChange={event => setAssetForm(prev => ({ ...prev, due_date: event.target.value }))}
                          required
                        />
                      </Form.Group>
                      <Form.Group className="field-stack span-8">
                        <Form.Label>Akun hutang</Form.Label>
                        <Form.Select
                          value={assetForm.payable_account_id}
                          onChange={event => setAssetForm(prev => ({ ...prev, payable_account_id: event.target.value }))}
                          required
                        >
                          <option value="">Pilih akun hutang</option>
                          {liabilityAccounts.map(account => (
                            <option key={account.account_id} value={account.account_id}>
                              {account.account_id} - {account.account_name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </>
                  ) : (
                    <>
                      <Form.Group className="field-stack span-4">
                        <Form.Label>Metode bayar</Form.Label>
                        <Form.Select
                          value={assetForm.payment_method}
                          onChange={event => setAssetForm(prev => ({ ...prev, payment_method: event.target.value }))}
                        >
                          {SETTLEMENT_OPTIONS.filter(option => option.value !== 'payable').map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                      <Form.Group className="field-stack span-8">
                        <Form.Label>Akun pembayaran</Form.Label>
                        <Form.Select
                          value={assetForm.payment_account_id}
                          onChange={event => setAssetForm(prev => ({ ...prev, payment_account_id: event.target.value }))}
                          required
                        >
                          <option value="">Pilih akun asset</option>
                          {assetAccounts.map(account => (
                            <option key={account.account_id} value={account.account_id}>
                              {account.account_id} - {account.account_name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </>
                  )}
                  <Form.Group className="field-stack span-12">
                    <Form.Label>Catatan</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={assetForm.notes}
                      onChange={event => setAssetForm(prev => ({ ...prev, notes: event.target.value }))}
                      placeholder="Catatan tambahan untuk audit aset."
                    />
                  </Form.Group>
                  <div className="field-stack span-12 accounting-action-stack">
                    <div className="d-flex flex-wrap gap-2">
                      <Button type="submit" disabled={!selectedOutlet || savingAssetDocument || assetAccounts.length === 0}>
                        <FaPlus /> {savingAssetDocument ? 'Menyimpan...' : 'Simpan dokumen aset'}
                      </Button>
                      <Button type="button" variant="outline-secondary" onClick={resetAssetForm}>
                        Reset form
                      </Button>
                    </div>
                  </div>
                </div>
              </Form>

              <div className="accounting-inline-note">
                Flow aset: `buat dokumen aset` -> satu dokumen bisa mencatat beberapa unit asset sekaligus -> jika hutang maka
                outstanding bertambah -> saat dibayar sistem membuat jurnal `debit hutang / credit kas-bank` tanpa masuk ke
                beban usaha.
              </div>
            </section>
          </div>
        ) : null}

        {activeWorkspace === 'payable' ? (
          <div className="accounting-panel-grid">
            <section className="section-card accounting-insight-card">
              <div className="accounting-section-head">
                <div>
                  <span className="page-eyebrow">Pembayaran hutang aset</span>
                  <h2>Lunasi pembelian aset yang outstanding</h2>
                  <p>Pilih dokumen pembelian aset yang masih tersisa. History payment dan saldo hutangnya akan diperbarui otomatis.</p>
                </div>
                <span className={`status-pill ${outstandingPayableDocuments.length ? 'is-warning' : ''}`}>
                  <FaFileInvoiceDollar /> {outstandingPayableDocuments.length} open bill
                </span>
              </div>

              <Form onSubmit={handleSavePayablePayment}>
                <div className="form-grid">
                  <Form.Group className="field-stack span-12">
                    <Form.Label>Dokumen hutang aset</Form.Label>
                    <Form.Select
                      value={payablePaymentForm.document_id}
                      onChange={event => setPayablePaymentForm(prev => ({ ...prev, document_id: event.target.value, amount: '' }))}
                      required
                    >
                      <option value="">Pilih hutang aset yang akan dibayar</option>
                      {outstandingPayableDocuments.map(item => (
                        <option key={item.id} value={item.id}>
                          {item.document_no} - {item.description} - sisa {formatCurrency(item.outstanding_amount)}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="field-stack span-4">
                    <Form.Label>Tanggal payment</Form.Label>
                    <Form.Control
                      type="date"
                      value={payablePaymentForm.payment_date}
                      onChange={event => setPayablePaymentForm(prev => ({ ...prev, payment_date: event.target.value }))}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="field-stack span-4">
                    <Form.Label>Nominal payment</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={payablePaymentForm.amount}
                      onChange={event => setPayablePaymentForm(prev => ({ ...prev, amount: event.target.value }))}
                      placeholder="0"
                      required
                    />
                  </Form.Group>
                  <Form.Group className="field-stack span-4">
                    <Form.Label>Metode bayar</Form.Label>
                    <Form.Select
                      value={payablePaymentForm.payment_method}
                      onChange={event => setPayablePaymentForm(prev => ({ ...prev, payment_method: event.target.value }))}
                    >
                      {SETTLEMENT_OPTIONS.filter(option => option.value !== 'payable').map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="field-stack span-12">
                    <Form.Label>Akun pembayaran</Form.Label>
                    <Form.Select
                      value={payablePaymentForm.payment_account_id}
                      onChange={event => setPayablePaymentForm(prev => ({ ...prev, payment_account_id: event.target.value }))}
                      required
                    >
                      <option value="">Pilih akun asset</option>
                      {assetAccounts.map(account => (
                        <option key={account.account_id} value={account.account_id}>
                          {account.account_id} - {account.account_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="field-stack span-12">
                    <Form.Label>Catatan payment</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={payablePaymentForm.notes}
                      onChange={event => setPayablePaymentForm(prev => ({ ...prev, notes: event.target.value }))}
                      placeholder="Catatan pembayaran hutang aset"
                    />
                  </Form.Group>
                  <div className="field-stack span-12 accounting-action-stack">
                    <div className="d-flex flex-wrap gap-2">
                      <Button type="submit" disabled={!selectedOutlet || savingAssetPayment || outstandingPayableDocuments.length === 0}>
                        <FaMoneyCheckAlt /> {savingAssetPayment ? 'Menyimpan...' : 'Simpan pembayaran hutang aset'}
                      </Button>
                      <Button type="button" variant="outline-secondary" onClick={resetPayablePaymentForm}>
                        Reset form
                      </Button>
                    </div>
                  </div>
                </div>
              </Form>

              <div className="accounting-inline-note">
                {selectedPayableDocument ? (
                  <>
                    Dokumen terpilih: <strong>{selectedPayableDocument.document_no}</strong> dengan outstanding{' '}
                    <strong>{formatCurrency(selectedPayableDocument.outstanding_amount)}</strong>.
                  </>
                ) : (
                  'Pilih dokumen hutang aset yang masih outstanding untuk men-generate jurnal pembayaran.'
                )}
              </div>
            </section>
          </div>
        ) : null}

        {activeWorkspace === 'policy' ? (
          <div className="accounting-panel-grid">
            <section className="section-card accounting-insight-card">
              <div className="accounting-section-head">
                <div>
                  <span className="page-eyebrow">Policy penyusutan</span>
                  <h2>Atur policy default untuk report aset</h2>
                  <p>Umur manfaat per kategori, residu default, dan start rule di sini langsung dipakai oleh Asset Register dan Depreciation Schedule.</p>
                </div>
                <span className={`status-pill ${depreciationPolicyError ? 'is-danger' : ''}`}>
                  <FaBalanceScale /> {loadingDepreciationPolicy ? 'Memuat...' : 'Policy live'}
                </span>
              </div>

              {depreciationPolicyError ? (
                <div className="accounting-inline-note mt-0">
                  {depreciationPolicyError}
                </div>
              ) : null}

              <Form onSubmit={handleSaveDepreciationPolicy}>
                <div className="form-grid">
                  <Form.Group className="field-stack span-4">
                    <Form.Label>Metode depresiasi</Form.Label>
                    <Form.Select
                      value={depreciationPolicyForm.method}
                      onChange={event => setDepreciationPolicyForm(prev => ({ ...prev, method: event.target.value }))}
                      disabled={loadingDepreciationPolicy || savingDepreciationPolicy}
                    >
                      {DEPRECIATION_METHOD_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="field-stack span-4">
                    <Form.Label>Mulai penyusutan</Form.Label>
                    <Form.Select
                      value={depreciationPolicyForm.depreciation_start_rule}
                      onChange={event => setDepreciationPolicyForm(prev => ({ ...prev, depreciation_start_rule: event.target.value }))}
                      disabled={loadingDepreciationPolicy || savingDepreciationPolicy}
                    >
                      {DEPRECIATION_START_RULE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="field-stack span-4">
                    <Form.Label>Residu default</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={depreciationPolicyForm.residual_value_default}
                      onChange={event => setDepreciationPolicyForm(prev => ({ ...prev, residual_value_default: event.target.value }))}
                      placeholder="0"
                      disabled={loadingDepreciationPolicy || savingDepreciationPolicy}
                    />
                  </Form.Group>

                  {depreciationPolicyForm.useful_life_defaults.map(item => (
                    <Form.Group key={item.category} className="field-stack span-4">
                      <Form.Label>{item.category_label || humanizeToken(item.category)}</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        step="1"
                        value={item.useful_life_months}
                        onChange={event => handleDepreciationUsefulLifeChange(item.category, event.target.value)}
                        disabled={loadingDepreciationPolicy || savingDepreciationPolicy}
                      />
                      <small className="text-muted">{formatDepreciationUsefulLifeLabel(item.useful_life_months)}</small>
                    </Form.Group>
                  ))}

                  <div className="field-stack span-12 accounting-action-stack">
                    <div className="d-flex flex-wrap gap-2">
                      <Button type="submit" disabled={loadingDepreciationPolicy || savingDepreciationPolicy}>
                        <FaPlus /> {savingDepreciationPolicy ? 'Menyimpan...' : 'Simpan policy penyusutan'}
                      </Button>
                      <Button type="button" variant="outline-secondary" onClick={resetDepreciationPolicyForm} disabled={loadingDepreciationPolicy || savingDepreciationPolicy}>
                        Reset perubahan
                      </Button>
                      <Button type="button" variant="outline-secondary" onClick={() => authToken && fetchDepreciationPolicy(authToken)} disabled={!authToken || loadingDepreciationPolicy || savingDepreciationPolicy}>
                        <FaCheckCircle /> Muat ulang policy
                      </Button>
                    </div>
                  </div>
                </div>
              </Form>

              <div className="accounting-inline-note">
                Kategori yang tidak dikirim atau belum terisi aman karena backend tetap merge dengan default fallback. Policy ini bersifat global dan belum dibedakan per outlet.
              </div>
            </section>

            <section className="section-card accounting-insight-card">
              <div className="accounting-section-head">
                <div>
                  <span className="page-eyebrow">Preview policy</span>
                  <h2>Ringkasan policy aktif</h2>
                  <p>Gunakan ringkasan ini untuk sanity check sebelum buka report penyusutan.</p>
                </div>
                <span className="status-pill">
                  <FaChartLine /> Estimation basis
                </span>
              </div>

              <div className="accounting-mini-stats">
                <div className="accounting-mini-stat">
                  <strong>{getDepreciationMethodLabel(depreciationPolicyForm.method)}</strong>
                  <small>Metode</small>
                </div>
                <div className="accounting-mini-stat">
                  <strong>{getDepreciationStartRuleLabel(depreciationPolicyForm.depreciation_start_rule)}</strong>
                  <small>Start rule</small>
                </div>
                <div className="accounting-mini-stat">
                  <strong>{formatCurrency(depreciationPolicyForm.residual_value_default)}</strong>
                  <small>Residu default</small>
                </div>
                <div className="accounting-mini-stat">
                  <strong>{depreciationPolicyForm.useful_life_defaults.length}</strong>
                  <small>Kategori policy</small>
                </div>
              </div>

              <div className="accounting-breakdown-list">
                {depreciationPolicyForm.useful_life_defaults.map(item => (
                  <div key={item.category} className="accounting-breakdown-item">
                    <span>{item.category_label || humanizeToken(item.category)}</span>
                    <strong>{formatDepreciationUsefulLifeLabel(item.useful_life_months)}</strong>
                  </div>
                ))}
              </div>

              <div className="d-flex flex-wrap gap-2 mt-3">
                <Button
                  variant="primary"
                  onClick={() => navigate(buildAccountingReportCenterPath('depreciation-schedule', { outletId: selectedOutlet }))}
                  disabled={!canOpenAnalyticsCenter}
                >
                  <FaChartLine /> Buka Depreciation Schedule
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate(buildAccountingReportCenterPath('asset-register', { outletId: selectedOutlet }))}
                  disabled={!canOpenAnalyticsCenter}
                >
                  <FaBuilding /> Buka Asset Register
                </Button>
              </div>
            </section>
          </div>
        ) : null}

        {activeWorkspace === 'overview' ? (
          <div className="accounting-panel-grid">
            <section className="section-card accounting-insight-card">
              <div className="accounting-section-head">
                <div>
                  <span className="page-eyebrow">Owner snapshot</span>
                  <h2>Posisi aset bulan ini</h2>
                  <p>Ringkasan ini fokus ke pembelian aset, nilai perolehan, dan hutang aset yang masih terbuka.</p>
                </div>
                <span className="status-pill">
                  <FaChartLine /> {currentPeriod.label}
                </span>
              </div>

              <div className="accounting-mini-stats">
                <div className="accounting-mini-stat">
                  <strong>{formatCurrency(assetSummary?.period_acquisition_total)}</strong>
                  <small>Perolehan bulan ini</small>
                </div>
                <div className="accounting-mini-stat">
                  <strong>{formatCurrency(assetSummary?.capitalized_total)}</strong>
                  <small>Nilai perolehan</small>
                </div>
                <div className="accounting-mini-stat">
                  <strong>{formatCurrency(assetSummary?.paid_total)}</strong>
                  <small>Sudah dibayar</small>
                </div>
                <div className="accounting-mini-stat">
                  <strong>{formatCurrency(assetSummary?.outstanding_total)}</strong>
                  <small>Outstanding hutang aset</small>
                </div>
                <div className="accounting-mini-stat">
                  <strong>{assetSummary?.open_bills || 0}</strong>
                  <small>Open bill</small>
                </div>
              </div>

              <div className="d-flex flex-wrap gap-2 mt-3">
                <Button
                  variant="primary"
                  onClick={() => navigate(buildAccountingReportCenterPath('balance-sheet', { outletId: selectedOutlet }))}
                  disabled={!canOpenAnalyticsCenter}
                >
                  <FaBalanceScale /> Balance Sheet
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => navigate(buildAccountingReportCenterPath('trial-balance', { outletId: selectedOutlet }))}
                  disabled={!canOpenAnalyticsCenter}
                >
                  <FaChartLine /> Trial Balance
                </Button>
              </div>
            </section>

            <section className="section-card accounting-insight-card">
              <div className="accounting-section-head">
                <div>
                  <span className="page-eyebrow">Report flow</span>
                  <h2>Alur review aset</h2>
                  <p>Review owner dibuat bertahap agar pembelian aset tidak tercampur dengan pengeluaran rutin.</p>
                </div>
                <span className="status-pill">
                  <FaBalanceScale /> Asset flow
                </span>
              </div>

              <div className="accounting-report-flow">
                {OWNER_REPORT_FLOW.map(item => (
                  <div key={item.title} className="accounting-report-flow__item">
                    <strong>{item.title}</strong>
                    <small>{item.copy}</small>
                  </div>
                ))}
              </div>

              <div className="accounting-breakdown-list">
                {(assetSummary?.category_breakdown || []).slice(0, 6).map(item => (
                  <div key={item.category} className="accounting-breakdown-item">
                    <span>{humanizeToken(item.category)}</span>
                    <strong>{formatCurrency(item.total)}</strong>
                  </div>
                ))}
                {!(assetSummary?.category_breakdown || []).length ? (
                  <div className="accounting-inline-note mt-0">Belum ada pembelian aset pada periode ini.</div>
                ) : null}
              </div>
            </section>
          </div>
        ) : null}

        {activeWorkspace === 'report' ? (
          <>
            <section className="section-card table-card">
              <div className="table-toolbar">
                <span className="status-chip">
                  <FaBuilding /> {assetDocuments.length} dokumen aset terbaru
                </span>
                <span className="status-chip">
                  <FaFileInvoiceDollar /> {assetSummary?.open_bills || 0} hutang aset masih terbuka
                </span>
              </div>
              <div className="accounting-inline-note mt-0 mb-3">
                Gunakan tombol hapus hanya untuk salah input. Dokumen hutang aset yang sudah punya payment tetap harus dibersihkan dulu dari histori pembayarannya.
              </div>
              {hasLegacyAssetJournalsWithoutDocuments ? (
                <div className="accounting-inline-note mt-0 mb-3">
                  Terlihat ada jurnal aset lama, tetapi belum ada dokumen aset di modul finance yang baru. Artinya data lama kemungkinan
                  diposting langsung ke jurnal atau berasal dari alur sebelum modul `Assets & Investments` aktif, sehingga belum bisa muncul
                  di list dokumen dan belum punya tombol hapus dari halaman ini.
                </div>
              ) : null}
	              <AdminDataTable
	                columns={assetDocumentColumns}
                data={assetDocuments}
                progressPending={loading}
                pagination
                highlightOnHover
                striped
                dense
                emptyTitle="Belum ada dokumen aset"
                emptyDescription={
                  hasLegacyAssetJournalsWithoutDocuments
                    ? 'Jurnal aset lama sudah ada, tetapi belum terhubung ke dokumen aset di modul finance baru.'
                    : 'Pembelian bangunan, alat gym, renovasi, dan inventaris besar akan muncul di sini.'
                }
              />
            </section>

            <div className="accounting-panel-grid">
              <section className="section-card table-card">
                <div className="table-toolbar">
                  <span className="status-chip">
                    <FaMoneyCheckAlt /> {assetPayments.length} payment hutang aset terbaru
                  </span>
                </div>
                <AdminDataTable
                  columns={assetPaymentColumns}
                  data={assetPayments}
                  progressPending={loading}
                  pagination
                  highlightOnHover
                  striped
                  dense
                  emptyTitle="Belum ada pembayaran hutang aset"
                  emptyDescription="Payment terhadap pembelian aset secara hutang akan tercatat di sini."
                />
              </section>

              <section className="section-card table-card">
                <div className="table-toolbar">
                  <span className="status-chip">
                    <FaCheckCircle /> {(assetSummary?.recent_journals || []).length} jurnal aset terbaru
                  </span>
                </div>
                <AdminDataTable
                  columns={recentJournalColumns}
                  data={assetSummary?.recent_journals || []}
                  progressPending={loading}
                  pagination
                  highlightOnHover
                  striped
                  dense
                  emptyTitle="Belum ada jurnal aset"
                  emptyDescription="Aktivitas jurnal pembelian dan pembayaran aset terbaru akan tampil di sini."
                />
              </section>
            </div>
          </>
        ) : null}
      </Container>
    </div>
  );
}

export default AssetManagementManager;
